import { useMutation, useQuery } from '@tanstack/react-query'
import type { DictationReviewSnapshot, DictationTone } from '@viapeople/components'
import { Button, SpinnerWithBackground, Text, TextArea } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { trpc } from '../../../utils/trpc'
import { DictationReviewModal } from '../../dictation/DictationReviewModal'

/**
 * @fileoverview Loop Response Page
 *
 * Page for participants to submit their loop survey responses
 * Accessed via magic link from email or from task list
 */

export function LoopExecution() {
	const { executionId } = useParams<{ executionId: string }>()
	const [textResponse, setTextResponse] = useState('')
	const [ratingResponse, setRatingResponse] = useState<number | null>(null)
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [dictationSnapshot, setDictationSnapshot] = useState<DictationReviewSnapshot | null>(null)
	const [isReviewOpen, setIsReviewOpen] = useState(false)
	const { isEnabled: dictationEnabled } = useFeatureFlag('voice-dictation')

	// Fetch execution details (via public/client endpoint)
	const { data: execution, isLoading: isLoadingExecution } = useQuery({
		...trpc.client.loops.getMyResponse.queryOptions({
			executionId: executionId ?? '',
		}),
		enabled: !!executionId,
	})

	const submitMutation = useMutation({
		...trpc.client.loops.submitResponse.mutationOptions(),
		onSuccess: () => {
			setIsSubmitted(true)
		},
		onError: (error) => {
			const message = error instanceof Error ? error.message : 'Failed to submit response. Please try again.'
			alert(message)
		},
	})
	const tokenMutation = useMutation({
		...trpc.client.tasks.requestDictationSessionToken.mutationOptions(),
	})
	const rewriteMutation = useMutation({
		...trpc.client.tasks.rewriteDictatedText.mutationOptions(),
	})

	const handleRatingClick = (rating: number) => {
		setRatingResponse(rating)
	}

	const handleSubmit = async () => {
		if (!execution) return

		// Validation
		const needsText = execution.loop?.textQuestionEnabled
		const needsRating = execution.loop?.ratingQuestionEnabled

		if (needsText && !textResponse.trim()) {
			alert('Please provide a text response')
			return
		}

		if (needsRating && ratingResponse === null) {
			alert('Please select a rating')
			return
		}

		if (dictationEnabled && dictationSnapshot) {
			setIsReviewOpen(true)
			return
		}

		await submitMutation.mutateAsync({
			executionId: executionId ?? '',
			textResponse: needsText ? textResponse.trim() : undefined,
			ratingResponse: needsRating && ratingResponse !== null ? ratingResponse : undefined,
		})
	}

	if (isLoadingExecution) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<SpinnerWithBackground />
			</div>
		)
	}

	if (!execution) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<Text variant='large' weight='semibold' className='text-red-600 mb-4'>
						Survey Not Found
					</Text>
					<Text variant='small' className='text-gray-600'>
						This survey link is invalid or has expired.
					</Text>
				</div>
			</div>
		)
	}

	if (isSubmitted || execution.status === 'completed') {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<div className='mb-6'>
						<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-green-600'
								fill='none'
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth='2'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-label='Success checkmark'>
								<title>Success</title>
								<path d='M5 13l4 4L19 7'></path>
							</svg>
						</div>
						<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
							Thank You!
						</Text>
						<Text variant='small' className='text-gray-600'>
							Your feedback has been submitted successfully.
						</Text>
					</div>
					<Text variant='xsmall' className='text-gray-500'>
						You can close this window now.
					</Text>
				</div>
			</div>
		)
	}

	const loop = execution.loop
	if (!loop) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<Text variant='medium' className='text-red-600'>
					Loop configuration not found
				</Text>
			</div>
		)
	}

	const getRatingLabel = (rating: number) => {
		const scale = loop.ratingScale || 10
		if (rating === 0) return 'Poor'
		if (rating === scale) return 'Excellent'
		if (rating >= scale * 0.8) return 'Very Good'
		if (rating >= scale * 0.6) return 'Good'
		if (rating >= scale * 0.4) return 'Fair'
		return 'Below Average'
	}

	const getRatingColor = (rating: number) => {
		const scale = loop.ratingScale || 10
		if (rating >= scale * 0.8) return 'bg-green-500 hover:bg-green-600'
		if (rating >= scale * 0.6) return 'bg-blue-500 hover:bg-blue-600'
		if (rating >= scale * 0.4) return 'bg-yellow-500 hover:bg-yellow-600'
		if (rating >= scale * 0.2) return 'bg-orange-500 hover:bg-orange-600'
		return 'bg-red-500 hover:bg-red-600'
	}

	return (
		<>
			<div className='min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 py-12 px-4 sm:px-6 lg:px-8'>
				<div className='max-w-3xl mx-auto'>
					<div className='bg-white rounded-lg shadow-xl overflow-hidden'>
						{/* Header */}
						<div className='bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6'>
							<Text variant='large' weight='semibold' className='text-white mb-2 text-2xl'>
								{loop.name}
							</Text>
							{loop.description && (
								<Text variant='small' className='text-purple-100'>
									{loop.description}
								</Text>
							)}
						</div>

						{/* Content */}
						<div className='px-8 py-8 space-y-8'>
							{/* Text Question */}
							{loop.textQuestionEnabled && loop.textQuestionLabel && (
								<div>
									<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
										{loop.textQuestionLabel}
									</Text>
									<TextArea
										value={textResponse}
										onChange={(e) => setTextResponse(e.target.value)}
										placeholder='Share your thoughts...'
										rows={5}
										dictationFieldId={`loop-${executionId}-text`}
										dictationEnabled={dictationEnabled}
										allowAiRewrite={dictationEnabled}
										onRequestDictationToken={async () => await tokenMutation.mutateAsync({ scope: 'dictation' })}
										onRewriteDictatedText={async (input: string, tone: DictationTone) =>
											(
												await rewriteMutation.mutateAsync({
													text: input,
													tone,
												})
											).rewrittenText
										}
										onDictationSnapshot={(snapshot) => setDictationSnapshot(snapshot)}
										className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent'
									/>
									<Text variant='xsmall' className='text-gray-500 mt-2'>
										Your feedback helps us improve
									</Text>
								</div>
							)}

							{/* Rating Question */}
							{loop.ratingQuestionEnabled && loop.ratingQuestionLabel && loop.ratingScale && (
								<div>
									<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
										{loop.ratingQuestionLabel}
									</Text>
									<Text variant='small' className='text-gray-600 mb-4'>
										On a scale from 0 to {loop.ratingScale}
									</Text>

									{/* Rating Scale */}
									<div>
										<div
											className={`grid gap-2 ${
												loop.ratingScale === 5
													? 'grid-cols-6'
													: loop.ratingScale === 10
														? 'grid-cols-11'
														: 'grid-cols-11'
											}`}>
											{Array.from({ length: (loop.ratingScale || 10) + 1 }, (_, i) => i).map((rating) => (
												<button
													type='button'
													key={rating}
													onClick={() => handleRatingClick(rating)}
													className={`
                              relative aspect-square rounded-lg transition-all duration-200 transform
                              ${
																ratingResponse === rating
																	? `${getRatingColor(rating)} text-white scale-110 ring-4 ring-offset-2 ring-purple-400`
																	: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
															}
                              font-semibold text-lg flex items-center justify-center
                              focus:outline-none focus:ring-4 focus:ring-purple-300
                            `}>
													{rating}
												</button>
											))}
										</div>

										{/* Rating Labels */}
										<div className='flex justify-between mt-3 text-xs text-gray-600'>
											<span>Poor</span>
											<span>Excellent</span>
										</div>

										{/* Selected Rating Feedback */}
										{ratingResponse !== null && (
											<div className='mt-4 p-4 bg-purple-50 rounded-lg text-center'>
												<Text variant='small' weight='semibold' className='text-purple-900'>
													You selected: {ratingResponse} - {getRatingLabel(ratingResponse)}
												</Text>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Submit Button */}
							<div className='flex justify-end pt-4'>
								<Button
									variant='primary'
									onClick={handleSubmit}
									disabled={submitMutation.isPending}
									className='px-8 py-3 text-lg'>
									{submitMutation.isPending ? 'Submitting...' : 'Submit Response'}
								</Button>
							</div>
						</div>

						{/* Footer */}
						<div className='bg-gray-50 px-8 py-4 border-t border-gray-200'>
							<Text variant='xsmall' className='text-gray-600 text-center'>
								Your responses help us understand and improve our workplace
							</Text>
						</div>
					</div>
				</div>
			</div>
			<DictationReviewModal
				isOpen={isReviewOpen}
				snapshots={dictationSnapshot ? [dictationSnapshot] : []}
				onClose={() => setIsReviewOpen(false)}
				onConfirm={() => {
					setIsReviewOpen(false)
					submitMutation.mutate({
						executionId: executionId ?? '',
						textResponse: loop?.textQuestionEnabled ? textResponse.trim() : undefined,
						ratingResponse: loop?.ratingQuestionEnabled && ratingResponse !== null ? ratingResponse : undefined,
					})
				}}
			/>
		</>
	)
}
